import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';

interface Suggestion {
    lat: number;
    lon: number;
    displayName: string;
}

interface LocationSelectorProps {
    value: string;
    onChange: (name: string) => void;
    onUseGPS: () => void;
    onSelectSuggestion: (suggestion: Suggestion) => void;
    gpsLoading: boolean;
    suggestions: Suggestion[];
    error?: string | null;
    gpsError?: string | null;
    placeholder?: string;
    className?: string;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
    value,
    onChange,
    onUseGPS,
    onSelectSuggestion,
    gpsLoading,
    suggestions,
    error,
    gpsError,
    placeholder,
    className = ""
}) => {
    const { t } = useTranslation();
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const isAndroid = Capacitor.getPlatform() === 'android';

    const getDisplayValue = () => {
        if (value.startsWith('GPS:')) {
            const coords = value.substring(4);
            return `${t('planner.location.gpsPosition')} (${coords.replace(',', ', ')})`;
        }
        return value;
    };

    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <div className="relative group">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-primary">pin_drop</span>
                <input
                    type="text"
                    value={getDisplayValue()}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || t('planner.location.placeholder')}
                    className="w-full h-12 rounded-full pl-11 pr-12 bg-zinc-900 border border-zinc-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition text-sm text-white placeholder:text-zinc-500"
                />
                <button
                    type="button"
                    onClick={onUseGPS}
                    disabled={gpsLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-zinc-800 rounded-full text-primary disabled:opacity-50"
                    title={t('planner.location.gpsButtonTitle')}
                >
                    <span className={`material-symbols-outlined text-[20px] ${gpsLoading ? 'animate-spin' : ''}`}>
                        {gpsLoading ? 'progress_activity' : 'my_location'}
                    </span>
                </button>

                {/* Suggestions List */}
                {suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-800 border border-zinc-700 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="bg-zinc-900/50 px-3 py-2 border-b border-zinc-700/50 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">{t('planner.location.suggestionsTitle')}</span>
                            <span className="material-symbols-outlined text-[14px] text-primary">assistant</span>
                        </div>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar">
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={`${suggestion.lat}-${suggestion.lon}-${index}`}
                                    type="button"
                                    onClick={() => onSelectSuggestion(suggestion)}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 hover:text-white transition group border-b border-zinc-700/30 last:border-0"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="material-symbols-outlined text-[18px] text-zinc-500 group-hover:text-primary mt-0.5">location_on</span>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-zinc-300 group-hover:text-white font-medium line-clamp-2">
                                                {suggestion.displayName}
                                            </span>
                                            <span className="text-[9px] text-zinc-500 group-hover:text-primary/70 font-mono mt-0.5">
                                                {suggestion.lat.toFixed(4)}, {suggestion.lon.toFixed(4)}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Offline Warning for Android */}
            {isAndroid && isOffline && (
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-300">
                    <div className="flex items-start gap-2.5 text-cyan-200">
                        <span className="material-symbols-outlined text-[18px] shrink-0 mt-0.5 text-cyan-400">info</span>
                        <p className="text-[11px] leading-relaxed font-medium">
                            {t('planner.location.offlineSearchWarning')}
                        </p>
                    </div>
                </div>
            )}

            {/* Error Messages */}
            {error && (
                <p className="text-[10px] text-cyan-400 px-1 flex items-center gap-1 animate-in fade-in duration-300">
                    <span className="material-symbols-outlined text-[12px]">warning</span>
                    {error.includes('.') ? t(error) : error}
                </p>
            )}

            {gpsError && (
                <p className="text-[10px] text-red-400 px-1 flex items-center gap-1 animate-in fade-in duration-300">
                    <span className="material-symbols-outlined text-[12px]">gps_off</span>
                    {gpsError}
                </p>
            )}
        </div>
    );
};

